import React, { useState, useEffect } from 'react';
import { Download, Edit2, Check, Sparkles, FileText, Settings, Package, Trash2, Save, BookOpen, X, Eye, MessageSquare, PencilLine, Info } from 'lucide-react';
import FileUpload from '../../common/FileUpload';
import { modelServicePost, modelServiceUpload } from '../../../utils/modelService';
import { InfoModal } from '../../common/InfoModal';
import AskJproDrawer from './AskJproDrawer';

export default function DataExtraction() {
  const [files, setFiles] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldInput, setFieldInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [autoMode, setAutoMode] = useState(false);
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [modalPatternFields, setModalPatternFields] = useState([]);
  const [modalPatternName, setModalPatternName] = useState('');
  const [newFieldInput, setNewFieldInput] = useState('');
  const [showAddField, setShowAddField] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [askDrawer, setAskDrawer] = useState({ open: false, extractionId: null, filename: null, documentType: null });
  const [sheetInfo, setSheetInfo] = useState({});        // { [fileName]: { sheets:[{name,rows}], oversized } }
  const [selectedSheets, setSelectedSheets] = useState({}); // { [fileName]: [sheetName, ...] }

  // When Excel files are added, fetch their sheet list so we can offer a sheet picker
  // (optional for workbooks that fit; required when a workbook is too big).
  useEffect(() => {
    const isExcel = (f) => /\.(xlsx|xls|xlsm|xlsb|xltm|xltx)$/i.test(f.name);
    const pending = files.filter((f) => isExcel(f) && !(f.name in sheetInfo));
    if (pending.length === 0) return;
    (async () => {
      try {
        const fd = new FormData();
        pending.forEach((f) => fd.append('files', f));
        const resp = await modelServiceUpload('/api/extract-data/inspect', fd);
        if (!resp.ok) return;
        const data = await resp.json();
        const infoUpdate = {};
        const selUpdate = {};
        (data.files || []).forEach((info) => {
          if (info.is_workbook && info.sheets?.length) {
            infoUpdate[info.fileName] = info;
            // Default: a workbook that fits has all sheets selected (the picker is just
            // optional control); an oversized one starts empty so the user must choose.
            selUpdate[info.fileName] = info.oversized ? [] : info.sheets.map((s) => s.name);
          }
        });
        setSheetInfo((prev) => ({ ...prev, ...infoUpdate }));
        setSelectedSheets((prev) => ({ ...prev, ...selUpdate }));
      } catch {
        // Non-fatal: extraction still works without the picker.
      }
    })();
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved patterns on mount
  useEffect(() => {
    loadPatterns();
  }, []);

  const loadPatterns = async () => {
    try {
      // Using apiGet with automatic token refresh
      const { apiGet } = await import('../../../utils/api');
      const response = await apiGet('/extraction_patterns');
      if (response.ok) {
        const data = await response.json();
        setSavedPatterns(data.patterns || []);
      }
    } catch (error) {
      console.error('Error loading patterns:', error);
    }
  };

  const buildPromptFromFields = () => {
    if (fields.length === 0) return '';
    return `Extract the following fields:\n- ${fields.join('\n- ')}`;
  };

  // Parse a stored pattern prompt back into a tag list. Handles both the
  // bullet form ("Extract the following fields:\n- X\n- Y") and legacy
  // comma-separated free-text.
  const parsePromptToFields = (storedPrompt) => {
    const text = (storedPrompt || '').trim();
    if (!text) return [];
    const bulletMatch = text.match(/Extract the following fields:\s*\n([\s\S]+)/i);
    const block = bulletMatch ? bulletMatch[1] : text;
    return block
      .split(/\n|,|;/)
      .map((line) => line.replace(/^[-*•\s]+/, '').trim())
      .filter(Boolean);
  };

  const addField = (raw) => {
    const trimmed = (raw ?? fieldInput).trim();
    if (!trimmed) return;
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    setFields((prev) => {
      const seen = new Set(prev);
      const merged = [...prev];
      parts.forEach((p) => {
        if (!seen.has(p)) {
          seen.add(p);
          merged.push(p);
        }
      });
      return merged;
    });
    setFieldInput('');
  };

  const removeFieldAt = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleExtract = async () => {
    if (!autoMode && fields.length === 0) {
      alert('Add at least one field to extract, or switch to Auto Extract');
      return;
    }

    if (files.length === 0) {
      alert('Please upload files');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      if (autoMode) {
        formData.append('auto_extract', 'true');
      } else {
        formData.append('prompt', buildPromptFromFields());
      }

      // Focus specific workbook sheets if the user picked any (optional control, or
      // required for oversized workbooks). Sent as the union of chosen sheet names;
      // the backend keeps only matching sheets per file.
      const chosenSheets = [...new Set(Object.values(selectedSheets).flat())];
      if (chosenSheets.length) {
        formData.append('sheets', JSON.stringify(chosenSheets));
      }

      const response = await modelServiceUpload('/api/extract-data', formData);

      if (!response.ok) throw new Error('Extraction failed');

      const data = await response.json();
      setResults(data.results || []);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to extract data');
    } finally {
      setLoading(false);
    }
  };

  const savePattern = async () => {
    const name = window.prompt('Pattern name:');
    if (!name) return;

    try {
      // Using apiPost with automatic token refresh
      const { apiPost } = await import('../../../utils/api');
      const response = await apiPost('/extraction_patterns', {
        extraction_pattern: { name, prompt: buildPromptFromFields() }
      });

      if (response.ok) {
        loadPatterns();
        alert('Pattern saved successfully!');
      } else {
        const data = await response.json();
        alert(data.errors?.join(', ') || 'Failed to save pattern');
      }
    } catch (error) {
      console.error('Error saving pattern:', error);
      alert('Failed to save pattern');
    }
  };

  const loadPattern = (pattern) => {
    setFields(parsePromptToFields(pattern.prompt));
    setAutoMode(false);
  };

  const updateField = (resultIdx, fieldIdx, value) => {
    setResults(prev => {
      const updated = [...prev];
      updated[resultIdx].fields[fieldIdx].value = value;
      return updated;
    });
    setEditingField(null);
  };

  const deleteField = (resultIdx, fieldIdx) => {
    setResults(prev => {
      const updated = [...prev];
      updated[resultIdx].fields.splice(fieldIdx, 1);
      return updated;
    });
  };

  const openPatternModal = (result) => {
    // Extract unique field names from detected pattern
    const fieldNames = [...new Set(result.fields.map(f => f.field_key || f.name))];
    setModalPatternFields(fieldNames);
    setModalPatternName('');
    setShowPatternModal(true);
  };

  const removeFieldFromPattern = (fieldName) => {
    setModalPatternFields(prev => prev.filter(f => f !== fieldName));
  };

  const savePatternFromModal = async () => {
    if (!modalPatternName.trim()) {
      alert('Please enter a pattern name');
      return;
    }

    if (modalPatternFields.length === 0) {
      alert('Please select at least one field');
      return;
    }

    // Create a prompt from selected field names
    const generatedPrompt = `Extract the following fields:\n- ${modalPatternFields.join('\n- ')}`;

    try {
      // Using apiPost with automatic token refresh
      const { apiPost } = await import('../../../utils/api');
      const response = await apiPost('/extraction_patterns', {
        extraction_pattern: { name: modalPatternName, prompt: generatedPrompt }
      });

      if (response.ok) {
        loadPatterns();
        setShowPatternModal(false);
        setModalPatternName('');
        setModalPatternFields([]);
        alert('Pattern saved successfully!');
      } else {
        const data = await response.json();
        alert(data.errors?.join(', ') || 'Failed to save pattern');
      }
    } catch (error) {
      console.error('Error saving pattern:', error);
      alert('Failed to save pattern');
    }
  };

  const createCustomPattern = () => {
    setModalPatternFields([]);
    setModalPatternName('');
    setShowPatternModal(true);
  };

  const addFieldToPattern = () => {
    if (newFieldInput.trim()) {
      setModalPatternFields(prev => [...prev, newFieldInput.trim()]);
      setNewFieldInput('');
      setShowAddField(false);
    }
  };

  const exportData = async (format) => {
    try {
      const response = await modelServicePost('/api/export-data', { results, format });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `extracted_data.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error:', error);
      alert('Export failed');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-blue-100 dark:border-gray-700 px-6 py-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-rasa text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
              Document Intelligence
            </h1>
            <p className="text-sm font-unna text-gray-600 dark:text-gray-300 mt-1">Extract structured data from documents with JPRO</p>
          </div>
          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 shadow-sm transition-all text-xs"
          >
            <BookOpen className="w-3 h-3" />
            Guide
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">

            {/* Upload Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-blue-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md transition-shadow">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                <FileText size={14} className="text-blue-600 dark:text-blue-400" />
                Upload Files
              </h2>

              <FileUpload
                files={files}
                onFilesChange={setFiles}
                multiple={true}
                accept=".pdf,.docx,.xlsx,.xls,.ppt,.pptx,image/*"
                showFileList={true}
              />

              {/* Optional sheet picker for multi-sheet workbooks (required when oversized) */}
              {files.map((f) => {
                const info = sheetInfo[f.name];
                if (!info || !info.sheets || info.sheets.length <= 1) return null;
                const sel = selectedSheets[f.name] || [];
                const toggle = (name) =>
                  setSelectedSheets((prev) => {
                    const cur = prev[f.name] || [];
                    return { ...prev, [f.name]: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name] };
                  });
                return (
                  <div key={f.name} className="mt-3 rounded-lg border border-blue-100 dark:border-gray-700 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Sheets in {f.name}</span>
                      <span className="text-[11px] text-gray-400">{sel.length}/{info.sheets.length} selected</span>
                    </div>
                    {info.oversized && (
                      <p className="text-xs text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-2 py-1 mb-2">
                        This workbook is large. Choose the sheet(s) you want to extract.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {info.sheets.map((s) => {
                        const on = sel.includes(s.name);
                        return (
                          <button
                            key={s.name}
                            type="button"
                            onClick={() => toggle(s.name)}
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                              on
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-gray-600'
                            }`}
                          >
                            {s.name} <span className="opacity-70">({s.rows.toLocaleString()})</span>
                          </button>
                        );
                      })}
                    </div>
                    {!info.oversized && (
                      <p className="text-[11px] text-gray-400 mt-2">Optional: leave all selected to extract the whole workbook.</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extraction Prompt */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-purple-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md transition-shadow">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-3">
                <Settings size={14} className="text-purple-600 dark:text-purple-400" />
                Extraction Settings
              </h2>

              {/* Mode picker — explicit two-card choice */}
              <div className="mb-4">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  Choose how JPRO should extract
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Extraction mode">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={autoMode}
                    onClick={() => setAutoMode(true)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      autoMode
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-200 dark:ring-blue-900/40'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles size={14} className={autoMode ? 'text-blue-600 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'} />
                      <span className={`text-sm font-semibold ${autoMode ? 'text-blue-900 dark:text-blue-100' : 'text-gray-800 dark:text-gray-200'}`}>
                        Auto Extract
                      </span>
                      {autoMode && (
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      JPRO inspects the document and pulls every structured field it finds. Best for repeating forms or unfamiliar documents.
                    </p>
                  </button>

                  <button
                    type="button"
                    role="radio"
                    aria-checked={!autoMode}
                    onClick={() => setAutoMode(false)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      !autoMode
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 ring-2 ring-purple-200 dark:ring-purple-900/40'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <PencilLine size={14} className={!autoMode ? 'text-purple-600 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400'} />
                      <span className={`text-sm font-semibold ${!autoMode ? 'text-purple-900 dark:text-purple-100' : 'text-gray-800 dark:text-gray-200'}`}>
                        Custom Fields
                      </span>
                      {!autoMode && (
                        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-300">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      You tell JPRO exactly which fields to pull. Best when you know the data you need.
                    </p>
                  </button>
                </div>
              </div>

              {!autoMode && (
                <>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Fields to extract
                    {fields.length > 0 && (
                      <span className="ml-2 normal-case tracking-normal text-gray-400 dark:text-gray-500 font-normal">
                        ({fields.length})
                      </span>
                    )}
                  </label>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                    Type a field name and press <kbd className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-[10px] font-mono">Enter</kbd> to add it as a tag. You can also paste a comma-separated list.
                  </p>

                  <div
                    onClick={(e) => {
                      // Click anywhere in the chip area focuses the input
                      const input = e.currentTarget.querySelector('input');
                      input?.focus();
                    }}
                    className={`min-h-[42px] w-full flex flex-wrap gap-1.5 items-center border rounded px-2 py-1.5 bg-white dark:bg-gray-700 cursor-text transition-colors ${
                      fieldInput || fields.length > 0
                        ? 'border-purple-400 dark:border-purple-500'
                        : 'border-gray-200 dark:border-gray-600'
                    } focus-within:ring-1 focus-within:ring-purple-500 focus-within:border-purple-500`}
                  >
                    {fields.map((field, idx) => (
                      <span
                        key={`${field}-${idx}`}
                        className="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-800 px-2 py-0.5 rounded-full text-xs font-medium"
                      >
                        {field}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFieldAt(idx);
                          }}
                          className="hover:bg-purple-200 dark:hover:bg-purple-800/60 rounded-full p-0.5 transition-colors"
                          aria-label={`Remove ${field}`}
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={fieldInput}
                      onChange={(e) => setFieldInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          addField();
                        } else if (e.key === 'Backspace' && !fieldInput && fields.length > 0) {
                          removeFieldAt(fields.length - 1);
                        }
                      }}
                      onBlur={() => fieldInput.trim() && addField()}
                      placeholder={fields.length === 0 ? 'e.g. tenant name' : 'Add another…'}
                      className="flex-1 min-w-[120px] bg-transparent text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none [color-scheme:light] dark:[color-scheme:dark]"
                    />
                  </div>

                  {/* Saved Patterns Dropdown */}
                  {savedPatterns.length > 0 && (
                    <div className="mt-3">
                      <select
                        onChange={(e) => {
                          const pattern = savedPatterns.find(p => String(p.id) === e.target.value);
                          if (pattern) {
                            loadPattern(pattern);
                            e.target.value = ""; // Reset dropdown
                          }
                        }}
                        className="text-xs border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded px-2 py-1.5 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 [color-scheme:light] dark:[color-scheme:dark]"
                        value=""
                      >
                        <option value="">Load saved pattern...</option>
                        {savedPatterns.map(pattern => (
                          <option key={pattern.id} value={pattern.id}>
                            {pattern.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {autoMode && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 rounded">
                  <p className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-2">
                    <Sparkles size={14} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong className="text-blue-800 dark:text-blue-200">Auto Extract is on.</strong>{' '}
                      JPRO will scan each document, recognize its type, and pull every structured field it can find — no prompt needed.
                    </span>
                  </p>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleExtract}
                  disabled={loading || !files.length || (!autoMode && fields.length === 0)}
                  className={`px-4 py-1.5 text-white rounded text-xs font-medium shadow-sm transition-all ${
                    loading
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse cursor-wait'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed'
                  }`}
                >
                  {loading ? 'Extracting...' : 'Extract Data'}
                </button>
                <button
                  onClick={createCustomPattern}
                  className="px-3 py-1.5 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-50 dark:hover:bg-gray-700 text-xs font-medium transition-colors"
                >
                  Create Pattern
                </button>
                {!autoMode && (
                  <button
                    onClick={savePattern}
                    disabled={fields.length === 0}
                    className="px-3 py-1.5 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-400 rounded hover:bg-purple-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                  >
                    Save Current
                  </button>
                )}
              </div>
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-green-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <Package size={14} className="text-green-600 dark:text-green-400" />
                    Extracted Data
                  </h2>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => exportData('csv')}
                      className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded text-xs font-medium hover:from-green-600 hover:to-emerald-700 shadow-sm transition-all"
                    >
                      <Download size={11} />
                      CSV
                    </button>
                    <button
                      onClick={() => exportData('excel')}
                      className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded text-xs font-medium hover:from-blue-600 hover:to-blue-700 shadow-sm transition-all"
                    >
                      <Download size={11} />
                      Excel
                    </button>
                    <button
                      onClick={() => exportData('word')}
                      className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded text-xs font-medium hover:from-purple-600 hover:to-purple-700 shadow-sm transition-all"
                    >
                      <Download size={11} />
                      Word
                    </button>
                    <button
                      onClick={() => {
                        setResults([]);
                        setFiles([]);
                        setFields([]);
                        setFieldInput('');
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs font-medium shadow-sm transition-all"
                      title="Clear results and start new extraction"
                    >
                      <X size={11} />
                      Clear
                    </button>
                  </div>
                </div>

                <div className="max-h-[600px] overflow-y-auto p-5 space-y-4">
                  {results.map((result, resultIdx) => (
                    <div key={resultIdx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:border-blue-200 dark:hover:border-blue-700 transition-colors bg-white dark:bg-gray-800">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-blue-600 dark:text-blue-400 text-sm flex items-center gap-2">
                          <FileText size={14} />
                          {result.fileName}
                        </h3>
                        <div className="flex items-center gap-2">
                          {result.pattern_detected && (
                            <>
                              <span className="px-2.5 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full font-medium shadow-sm text-xs">
                                Pattern
                              </span>
                              <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full font-medium text-xs">
                                {result.record_count} records
                              </span>
                              <button
                                onClick={() => openPatternModal(result)}
                                className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full text-xs font-medium hover:from-purple-600 hover:to-purple-700 shadow-sm transition-all"
                              >
                                <Save size={12} />
                                Save
                              </button>
                            </>
                          )}
                          {result.extraction_id && (
                            <button
                              onClick={() => setAskDrawer({
                                open: true,
                                extractionId: result.extraction_id,
                                filename: result.fileName,
                                documentType: result.document_type,
                              })}
                              className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full text-xs font-medium hover:from-blue-600 hover:to-blue-700 shadow-sm transition-all"
                              title="Ask JPro about this document"
                            >
                              <MessageSquare size={12} />
                              Ask JPro
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Document recognition card */}
                      {(result.document_type || result.summary) && (
                        <div className="mb-4 p-3 rounded-md border border-blue-100 dark:border-blue-900/40 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                          <div className="flex items-start gap-2">
                            <Eye size={14} className="mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              {result.document_type && (
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-[10px] uppercase tracking-wide font-semibold text-blue-700 dark:text-blue-300">
                                    Recognized as
                                  </span>
                                  <span className="px-2 py-0.5 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 rounded text-xs font-medium">
                                    {result.document_type}
                                  </span>
                                </div>
                              )}
                              {result.summary && (
                                <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {result.summary}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Error (e.g. file too large) takes precedence over the data views */}
                      {result.status === 'error' ? (
                        <div className="flex items-start gap-2.5 p-3.5 rounded-md border border-orange-300 dark:border-orange-700 bg-blue-50 dark:bg-blue-900/20">
                          <Info size={16} className="mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-300" />
                          <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                            {result.error || 'This file could not be extracted.'}
                          </p>
                        </div>
                      ) : /* Show as table if pattern detected, otherwise show as grid */ result.pattern_detected ? (
                        // Table view for batch/pattern data
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-300 dark:border-gray-600">
                                <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">Row</th>
                                {/* Get unique column names */}
                                {[...new Set(result.fields.map(f => f.field_key || f.name.split(' - ')[1] || f.name))].map((colName, idx) => (
                                  <th key={idx} className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-600">
                                    {colName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </th>
                                ))}
                                <th className="w-16 px-2 py-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Group fields by row number
                                const rowData = {};
                                result.fields.forEach((field, fieldIdx) => {
                                  const rowNum = field.row || 1;
                                  if (!rowData[rowNum]) rowData[rowNum] = [];
                                  rowData[rowNum].push({ ...field, fieldIdx });
                                });

                                return Object.entries(rowData).map(([rowNum, rowFields]) => (
                                  <tr key={rowNum} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                                    <td className="px-3 py-2 font-medium text-gray-600 dark:text-gray-400 border-r border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                      {rowNum}
                                    </td>
                                    {rowFields.map((field) => (
                                      <td key={field.fieldIdx} className="px-3 py-2 border-r border-gray-100 dark:border-gray-700">
                                        {editingField === `${resultIdx}-${field.fieldIdx}` ? (
                                          <input
                                            type="text"
                                            defaultValue={typeof field.value === 'object' && field.value !== null ? field.value.value : field.value}
                                            onBlur={(e) => updateField(resultIdx, field.fieldIdx, e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                updateField(resultIdx, field.fieldIdx, e.target.value);
                                              } else if (e.key === 'Escape') {
                                                setEditingField(null);
                                              }
                                            }}
                                            autoFocus
                                            className="w-full border border-blue-500 dark:border-blue-400 bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 [color-scheme:light] dark:[color-scheme:dark]"
                                          />
                                        ) : (
                                          <div
                                            onClick={() => setEditingField(`${resultIdx}-${field.fieldIdx}`)}
                                            className="cursor-text min-h-[24px] flex items-center text-gray-800 dark:text-gray-200"
                                          >
                                            {typeof field.value === 'object' && field.value !== null
                                              ? field.value.value || JSON.stringify(field.value)
                                              : field.value || <span className="text-gray-400 dark:text-gray-500 italic text-[10px]">empty</span>}
                                          </div>
                                        )}
                                      </td>
                                    ))}
                                    <td className="px-2 py-2 text-right">
                                      <button
                                        onClick={() => {
                                          // Delete all fields in this row
                                          const fieldsToDelete = rowFields.map(f => f.fieldIdx).sort((a, b) => b - a);
                                          setResults(prev => {
                                            const updated = [...prev];
                                            fieldsToDelete.forEach(idx => {
                                              updated[resultIdx].fields.splice(idx, 1);
                                            });
                                            return updated;
                                          });
                                        }}
                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded transition-all"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        // Grid view for single document data
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {result.fields.map((field, fieldIdx) => (
                            <div key={fieldIdx} className="space-y-1">
                              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {field.name}
                              </label>

                              {editingField === `${resultIdx}-${fieldIdx}` ? (
                                <div className="flex gap-1">
                                  <input
                                    type="text"
                                    defaultValue={field.value}
                                    onBlur={(e) => updateField(resultIdx, fieldIdx, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        updateField(resultIdx, fieldIdx, e.target.value);
                                      }
                                    }}
                                    autoFocus
                                    className="flex-1 border border-blue-500 dark:border-blue-400 bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 [color-scheme:light] dark:[color-scheme:dark]"
                                  />
                                  <button
                                    onClick={() => setEditingField(null)}
                                    className="px-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded hover:from-blue-600 hover:to-blue-700 shadow-sm transition-all"
                                  >
                                    <Check size={13} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 group">
                                  <div className="flex-1 border border-gray-200 dark:border-gray-600 rounded px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 dark:text-gray-200">
                                    {field.value || <span className="text-gray-400 dark:text-gray-500">No data</span>}
                                  </div>
                                  <button
                                    onClick={() => setEditingField(`${resultIdx}-${fieldIdx}`)}
                                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded transition-all"
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={() => deleteField(resultIdx, fieldIdx)}
                                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded transition-all"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Pattern Modal */}
      {showPatternModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 text-white">
              <h3 className="text-xl font-semibold">Create Extraction Pattern</h3>
              <p className="text-sm text-purple-100 mt-1">Define fields/columns to extract from documents</p>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pattern Name
                </label>
                <input
                  type="text"
                  value={modalPatternName}
                  onChange={(e) => setModalPatternName(e.target.value)}
                  placeholder="e.g., Invoice Data, Customer Information, etc."
                  className="w-full border-2 border-gray-200 dark:border-gray-600 bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-400 [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fields to Extract ({modalPatternFields.length})
                  </label>
                  <button
                    onClick={() => setShowAddField(!showAddField)}
                    className="text-xs px-3 py-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full hover:from-purple-600 hover:to-purple-700 transition-all"
                  >
                    + Add Field
                  </button>
                </div>

                {/* Add Field Input */}
                {showAddField && (
                  <div className="mb-3 flex gap-2">
                    <input
                      type="text"
                      value={newFieldInput}
                      onChange={(e) => setNewFieldInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          addFieldToPattern();
                        }
                      }}
                      placeholder="e.g., customer_name, invoice_number, date"
                      className="flex-1 border-2 border-purple-200 dark:border-purple-700 bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-400 [color-scheme:light] dark:[color-scheme:dark]"
                      autoFocus
                    />
                    <button
                      onClick={addFieldToPattern}
                      disabled={!newFieldInput.trim()}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-300 text-sm font-medium transition-all"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddField(false);
                        setNewFieldInput('');
                      }}
                      className="px-4 py-2 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {modalPatternFields.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500 border-2 border-dashed border-gray-200 dark:border-gray-600 rounded-lg">
                    No fields yet. Click "Add Field" to start.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto">
                    {modalPatternFields.map((field, idx) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-sm group"
                      >
                        <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                          {idx + 1}
                        </span>
                        <span>{field}</span>
                        <button
                          onClick={() => removeFieldFromPattern(field)}
                          className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-400">
                <strong>Note:</strong> These fields will be extracted as columns when processing similar documents.
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPatternModal(false);
                  setModalPatternName('');
                  setModalPatternFields([]);
                }}
                className="px-4 py-2 border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={savePatternFromModal}
                disabled={!modalPatternName.trim() || modalPatternFields.length === 0}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed text-sm font-medium shadow-sm transition-all"
              >
                Save Pattern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Guide Modal */}
      <InfoModal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Document Intelligence Guide"
      >
        <div className="space-y-4 font-rasa">
          {/* Quick Start */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Quick Start</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Upload PDF, DOCX, XLSX, or image files</li>
              <li>Choose Manual mode (describe what to extract) or Auto mode (AI decides)</li>
              <li>Optionally load a saved extraction pattern</li>
              <li>Click "Extract Data"</li>
              <li>Review the recognized document type and summary for each file</li>
              <li>Edit values inline, then export as CSV, Excel, or Word</li>
              <li>Click "Ask JPro" on any result to chat with the document</li>
            </ol>
          </section>

          {/* Extraction Modes */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Extraction Modes</h4>
            <div className="space-y-2 text-sm">
              <div>
                <strong className="text-gray-900 dark:text-white">Manual Mode:</strong>
                <p className="ml-2">Describe what to extract (e.g., "Extract names, emails, and amounts")</p>
              </div>
              <div>
                <strong className="text-gray-900 dark:text-white">Auto Extract:</strong>
                <p className="ml-2">AI automatically detects repeating patterns and extracts key data fields</p>
              </div>
            </div>
          </section>

          {/* Document Recognition */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Document Recognition</h4>
            <p className="mb-2 text-sm">Every extraction now includes an AI-generated recognition card for each file:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
              <li><strong>Recognized As:</strong> A short, open-ended label describing what the document is (e.g., "Utility Authorization Form", "W-2 Wage Statement")</li>
              <li><strong>Summary:</strong> A 1-2 sentence excerpt of the document's purpose and standout details (dates, parties, totals)</li>
            </ul>
            <p className="mt-2 text-sm">Use this to quickly verify you uploaded the right file before reviewing the extracted data.</p>
          </section>

          {/* Ask JPro */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Ask JPro About a Document</h4>
            <p className="mb-2 text-sm">Each extracted result has an <strong>Ask JPro</strong> button. Click it to open a chat drawer scoped to that single document.</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
              <li>Ask follow-up questions: "Who signed this?", "What is the effective date?", "Summarize the key obligations"</li>
              <li>Pick from suggested prompts: Summarize, Key dates, People & parties, Action required</li>
              <li>The chat is <strong>session-only</strong> — it resets when you refresh or re-extract</li>
              <li>For longer, persistent conversations, upload the document to AI Chat instead</li>
            </ul>
          </section>

          {/* Patterns */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Using Patterns</h4>
            <p className="mb-2 text-sm">Save extraction patterns for recurring document types:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
              <li>After extraction, click "Save as Pattern"</li>
              <li>Name the pattern (e.g., "Invoice Data", "Resume Fields")</li>
              <li>Add/remove specific fields to extract</li>
              <li>Reuse pattern for similar documents</li>
              <li>Delete patterns from the dropdown menu</li>
            </ul>
          </section>

          {/* Editing Results */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Editing Results</h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
              <li>Click any cell to edit extracted values</li>
              <li>Press Enter or click away to save; Escape to cancel</li>
              <li>Hover a row and click the trash icon to delete that row</li>
              <li>Changes are applied immediately to the export</li>
            </ul>
          </section>

          {/* Tips */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Tips for Best Results</h4>
            <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
              <li>Upload clear, high-quality document scans</li>
              <li>Be specific in manual prompts: "Extract company name, date, total amount"</li>
              <li>Use patterns for consistent document types (invoices, resumes, forms)</li>
              <li>Process multiple similar files in one batch</li>
              <li>Review extracted data — and the recognition summary — before downloading</li>
            </ul>
          </section>

          {/* Workflow */}
          <section>
            <h4 className="font-unna font-semibold text-lg mb-2 text-gray-900 dark:text-white">Typical Workflow</h4>
            <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm space-y-1">
              <p>1. Upload documents</p>
              <p>2. Select pattern or describe extraction</p>
              <p>3. Click "Extract Data"</p>
              <p>4. Check the recognition card (type + summary) for each file</p>
              <p>5. Review and edit results</p>
              <p>6. (Optional) Ask JPro questions about the document</p>
              <p>7. Save as pattern (optional)</p>
              <p>8. Download CSV, Excel, or Word</p>
            </div>
          </section>

          {/* Supported Files */}
          <section className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
            <p className="text-sm">
              <strong>Supported Files:</strong> PDF, DOCX, XLSX, XLS, PPTX, Images (JPG, PNG)
            </p>
          </section>
        </div>
      </InfoModal>

      <AskJproDrawer
        isOpen={askDrawer.open}
        onClose={() => setAskDrawer((p) => ({ ...p, open: false }))}
        extractionId={askDrawer.extractionId}
        filename={askDrawer.filename}
        documentType={askDrawer.documentType}
      />
    </div>
  );
}
